import { faComment } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useRelevantTraps } from './useRelevantTraps';

export const useTrappingZoneThresholds = () => {
  const getRelevantTraps = useRelevantTraps();
  /**
   * Calculates threshold amount, accumulation and average as well as providing
   * JSX for displaying trap catch amount and accumulation.
   *
   * @param {object} trappingResult
   * @param {object} selectedInsectGroup
   * @param {object} relevantTraps
   * @returns Object that contains threshold amount, threshold accumulation,
   * average threshold (function), average amount (function), the lures and
   * bases changed and JSX for trap catch and accumulation.
   */
  const calculateThresholds = (
    trappingZone,
    trappingResult,
    selectedInsectGroup
  ) => {
    const amounts = [];
    let amountThresholdsMet = 0;
    let accumulationThresholdsMet = 0;

    const averageAmount = () =>
      trappingResult.trap_averages?.amount?.toFixed(2);

    const averageThresholdMet = () =>
      trappingResult.trap_averages?.threshold_met;

    const luresChanged = trappingResult.trap_monitoring_records.filter(
      (record) => record.lure_changed
    ).length;

    const basesChanged = trappingResult.trap_monitoring_records.filter(
      (record) => record.base_changed
    ).length;

    const relevantTraps = getRelevantTraps(trappingZone, selectedInsectGroup);

    const trapCells = relevantTraps.map((trap) => {
      const trappingResultTrap = trappingResult.traps[trap.id];
      const monitoringRecord = trappingResult.trap_monitoring_records.find(
        (record) => record.trap.id === trap.id
      );
      const comments =
        monitoringRecord && monitoringRecord.comments
          ? monitoringRecord.comments
          : null;
      const amount = trappingResultTrap ? trappingResultTrap.amount : null;
      const accumulation = trappingResultTrap
        ? trappingResultTrap.accumulation
        : null;

      const amountThresholdMet = trappingResultTrap
        ? trappingResultTrap.pre_trap_catch_threshold
        : null;
      const accumulationThresholdMet = trappingResultTrap
        ? trappingResultTrap.accumulation_threshold_met
        : null;

      amounts.push(amount);

      if (amountThresholdMet) amountThresholdsMet++;
      if (accumulationThresholdMet) accumulationThresholdsMet++;

      return (
        <Fragment key={trap.id}>
          <td
            style={{ borderLeft: '2px solid #dee2e6' }}
            className={amountThresholdMet ? 'text-danger' : null}
          >
            {amount === null ? '-' : amount}
            {comments ? (
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{comments}</Tooltip>}
              >
                <FontAwesomeIcon
                  className='text-primary ml-2'
                  icon={faComment}
                />
              </OverlayTrigger>
            ) : null}
          </td>
          <td
            className={`${accumulationThresholdMet ? 'text-danger' : null} ${
              comments ? 'pr-0' : null
            }`}
          >
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>Weekly Catch Accumulation</Tooltip>}
            >
              <i
                className={`${
                  trappingResult.threshold_met && accumulationThresholdMet
                    ? 'text-danger'
                    : 'text-muted'
                }`}
              >
                {accumulation === null ? '-' : `(${accumulation})`}
              </i>
            </OverlayTrigger>
          </td>
        </Fragment>
      );
    });

    return {
      amountThresholdsMet,
      accumulationThresholdsMet,
      averageThresholdMet,
      averageAmount,
      luresChanged,
      basesChanged,
      trapCells
    };
  };

  return { calculateThresholds };
};
